import pathToRegexp from 'path-to-regexp';
import { parse, stringify } from 'qs'; // eslint-disable-line import/no-extraneous-dependencies
import { __, compose, curry, isNil, merge, zipObj } from 'ramda';

// ----------------------------------------------------------------
// 字符串工具类 - 负责提供非业务耦合的各种字符串操作。
// ----------------------------------------------------------------

/**
 * 获取一个不会重复，自定义带scope的随机字符串(有利于项目维护等)。
 * RFC4122 version 4 compliant unique id creator.
 * Added by https://github.com/tufanbarisyildirim/
 *
 * @param id
 * @returns {string}
 */
const getGuId = (id) =>
  (id && id + '-') +
  'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });

/**
 * 设置url上面参数 RESTful 无encode
 * 备注: 一般的默认行为是encode，但是
 * @returns {string} 成功设置后的路径
 */
const toUrl = curry((url, obj) => {
  if (!url) {
    // eslint-disable-next-line
    throw 'Ineligible url to put RESTFul parameters.';
  }
  return pathToRegexp.compile(url, { encode: (value) => value })(obj);
});

/**
 * 设置url上面参数 RESTful
 * @returns {string} 成功设置后的路径
 */
const toUrlEnc = curry((url, obj) => {
  if (!url) {
    // eslint-disable-next-line
    throw 'Ineligible url to put RESTFul parameters.';
  }
  return pathToRegexp.compile(url)(obj);
});

/**
 * 获取url上面参数 RESTful
 * @param {string} url - 指定路径
 * @param {string} expression - 过滤参数的正则表达式
 * (详见https://github.com/pillarjs/path-to-regexp)
 * @returns {Object} - 参数数组，按顺序从前到后排
 */
const fromUrl = (url = window.location.href, expression) => {
  const keys = []; // 利用第二步操作对该数组对象进行填充
  const rules = pathToRegexp(expression, keys); // [{ name: 'first', ... }, { name: 'second', ...}]
  const pathArray = rules.exec(url) || []; // [url, str_1, str_2, ...]
  // 去除第一个解析项，默认为当前路径
  if (isNil(pathArray.shift())) {
    return {};
  }
  // = R.pipe(R.zip, R.fromPairs)
  return zipObj(
    keys.map((key) => key.name),
    pathArray.map((param) => window.decodeURIComponent(param)),
  );
};

/**
 * 设置url上面参数 query string
 * @returns {string}
 * @param {string} url - 指定路径
 * @param {Object} obj - 待反序列化的对象
 * @returns {string}
 */
const toQs = curry((url, obj) => {
  if (typeof url !== typeof '') {
    // eslint-disable-next-line
    throw 'Ineligible url to combine query string. Check your service call method.';
  }
  const compileUrl = [url, stringify(obj)].join(url.indexOf('?') !== -1 ? '&' : '?');
  return compileUrl.endsWith('?') ? compileUrl.substring(0, compileUrl.length - 1) : compileUrl;
});

/**
 * 获取url上面参数 query string
 * @param {string} url - 指定路径
 * @returns {Object} 这个函数参数只有1个，不做柯理化
 */
const fromQs = (url = window.location.href) => parse(url.split('?')[1]);

/**
 * 获取url上面参数 query string
 * @param {string} url - 指定路径
 * @param {Object} queryObject - 需要回填数据的对象
 * 注意: 为了实际应用中方便，该变量中的数值类型为自动转换 如果不希望自动转换 则请使用fromQs自行处理!!
 * @returns {Object}
 */
const fromQsToObj = curry((url = window.location.href, queryObject) =>
  compose(
    merge(queryObject, __),
    // R.mapObjIndexed(// 查询数字字符串自动转数字
    //   value => math.parseIfNumeric(value),
    //   R.__
    // )
  )(fromQs(url)),
);

/**
 * 从后台请求错误码 -> 国际化文件码
 * @param code - 错误返回码
 * @param scope - 作用领域
 * @return {string}
 */
const getErrCode = (code, scope) =>
  `${scope && scope + '.'}${code
    .split('_') // ^_^
    .map((item) => item.toLowerCase())
    .join('.')}`;

/**
 * 生成随机字符串
 * @param e - 字符串位数
 * @return {string}
 */

const randomString = (e) => {
  e = e || 32;
  var t = 'ABCDEFGHJKMNPQRSTWXYZabcdefhijkmnprstwxyz2345678',
    a = t.length,
    n = '';
  for (let i = 0; i < e; i++) n += t.charAt(Math.floor(Math.random() * a));
  return n;
};
export default {
  getGuId,
  toUrl,
  toUrlEnc,
  fromUrl,
  toQs,
  fromQs,
  fromQsToObj,
  getErrCode,
  randomString,
};
